/* AdminUserPreviewSection — "Preview as Student" god-view directory.
   Every link opens the REAL user-facing page. Backend + frontend bypass all
   locks, attempt caps and commit funnels for admin accounts only. */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, Layers, Briefcase, Bot, Wrench, Landmark, Rocket,
  ShieldCheck, LayoutDashboard, MessageSquare, Award, Home, IndianRupee,
  Users, HeartHandshake, BookOpen, ExternalLink,
} from 'lucide-react';

const GROUPS = [
  {
    label: 'The 9 Paths — exactly as students see them',
    items: [
      { to: '/career-clarity',            icon: GraduationCap, title: 'Career Clarity Tests', desc: 'Chooser: After 10th vs After Inter' },
      { to: '/career-clarity/after-10th', icon: GraduationCap, title: 'After 10th · 5 Group Quizzes', desc: 'MPC · BiPC · MEC · CEC · HEC' },
      { to: '/specializations',           icon: Layers,        title: 'Inner Specializations', desc: 'B.Tech · BBA · MBA funnel' },
      { to: '/job-roles',                 icon: Briefcase,     title: 'Future-Ready Job Roles', desc: 'Role commit-and-lock funnel' },
      { to: '/ai-pathway',                icon: Bot,           title: 'AI Pathway', desc: '17 departments · 125 AI roles' },
      { to: '/banking',                   icon: Landmark,      title: 'Banking Careers', desc: 'Family → roles funnel' },
      { to: '/blue-collar',               icon: Wrench,        title: 'Blue-Collar Jobs', desc: 'Category → careers funnel' },
      { to: '/ownpath',                   icon: Rocket,        title: 'Entrepreneurship · OwnPath', desc: 'Budget + state → ideas + schemes' },
      { to: '/build-badges',              icon: Award,         title: 'Build Badges', desc: 'Program → branch → project briefs' },
      { to: '/ai-risk-check',             icon: Bot,           title: 'Is AI Coming for Your Role?', desc: 'Stage → branch → 30-sec risk check' },
      { to: '/life-skills',               icon: HeartHandshake, title: 'Life Skills — For Every Future', desc: '320 skills · 9 categories · flip cards + full previews' },
    ],
  },
  {
    label: 'Student account pages',
    items: [
      { to: '/dashboard',    icon: LayoutDashboard, title: 'Dashboard', desc: 'Your admin dashboard shows all tiles open' },
      { to: '/my/path',      icon: BookOpen,        title: 'My Path (lean view)', desc: 'The kept-picks page users land on' },
      { to: '/ai-mentor',    icon: Bot,             title: 'AI Mentor (Lexi)', desc: 'Path-aware chat' },
      { to: '/previews',     icon: Layers,          title: 'Full Preview Library', desc: 'Admin-only full catalogue' },
    ],
  },
  {
    label: 'Public & conversion pages',
    items: [
      { to: '/',            icon: Home,           title: 'Homepage', desc: 'Hero + 9-card pathway grid' },
      { to: '/pricing',     icon: IndianRupee,    title: 'Pricing', desc: 'Plans + checkout entry' },
      { to: '/outcomes',    icon: Award,          title: 'Outcomes', desc: 'Verified student stories' },
    ],
  },
];

const AdminUserPreviewSection = () => (
  <div data-testid="admin-user-preview-section">
    <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3 mb-6">
      <ShieldCheck className="text-emerald-600 shrink-0 mt-0.5" size={18} />
      <p className="text-sm text-emerald-900">
        <b>God View is ON for your account.</b> Every link below opens the real user-facing page.
        As admin you bypass all commit-and-lock funnels, 2-attempt caps, path isolation and 423 lock screens —
        normal users still get the strict funnels.
      </p>
    </div>
    {GROUPS.map((g) => (
      <div key={g.label} className="mb-6">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">{g.label}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {g.items.map((it) => (
            <Link
              key={it.to + it.title}
              to={it.to}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-3 border border-gray-200 rounded-xl p-3.5 bg-gray-50 hover:bg-white hover:border-teal-300 hover:shadow-md transition-all"
              data-testid={`admin-preview-link-${it.to.replace(/\//g, '-') || 'home'}`}
            >
              <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 text-white flex items-center justify-center shrink-0">
                <it.icon size={16} />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 font-semibold text-sm text-gray-900 group-hover:text-teal-800">
                  {it.title}
                  <ExternalLink size={11} className="text-gray-400 group-hover:text-teal-600" />
                </span>
                <span className="block text-xs text-gray-500 truncate">{it.desc}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default AdminUserPreviewSection;
